import { Injectable } from '@angular/core';
import { ALIS_OUTBOUND_CONVERSATION, ALIS_SECURED_INBOUND_CONVERSATION, ALIS_UNKNOWN_INBOUND_CONVERSATION, ALIS_UNKNOWN_OUTBOUND_CONVERSATION, ALIS_UNSECURE_CALLBACK_CONVERSATION, ALIS_UNSECURED_INBOUND_CONVERSATION, ALIS_WEB_MESSAGE_CONVERSATION } from '../models/ALIS_Conversations';
import { RootScopeService } from './root-scope.service';
import { CustBusService } from './customer-search-bussiness-logic.service';
import * as Constants from './../models/Constants';
import _ from 'lodash';
import { GenesysCloudService } from './genesys-cloud.service';
import { EncryptDecryptService } from './encrypt-decrypt.service';

@Injectable({
  providedIn: 'root'
})
export class SimulationService {

  public simulationComponentRef: any;

  constructor(public rootService: RootScopeService,public customerService: CustBusService,public genesysService: GenesysCloudService,
    public encryptDecryptService: EncryptDecryptService,
  ) { }


  /**
   * Navigates to the customer accounts view and updates the application state with the provided customer data.
   *
   * This method performs the following actions:
   * - Deselects any previously selected item in the root service's items list.
   * - Selects the item corresponding to the customer search constant.
   * - Updates the active link in the root service.
   * - Prepares live call data by updating participant attributes with the provided customer information.
   * - Sets the live call state in the customer service.
   * - Generates a new conversation ID for the live call.
   * - Calls the Genesys service to identify the call as inbound or outbound.
   *
   * @param data - The data object containing customer information, typically with a `row` property that includes `clientId`, `platformExternalId`, and `udpId`.
   */
  routeToCustomerAccounts(data: any) {
    let items = [...this.rootService.items];
    let selectedObj: any = null;
    let earlierSelectedItem: any = _.find(
      items,
      (each) => each.selected == true
    );
    if(earlierSelectedItem?.['selected'])
    earlierSelectedItem['selected'] = false;
    selectedObj = _.find(
      items,
      (each) => each.id == Constants.CUSTOMER_SEARCH
    );
    selectedObj['selected'] = true;
    this.rootService.activeLink = selectedObj?.id;
    this.rootService.items = items;

     
    let pptAttributes={platformExternalId : data?.row?.platformExternalId, clientId :data?.row?.clientId, queueName:`${data?.row?.clientId}_${data?.row?.SIM_clientName}_${Constants.SIM_QUEUE_NAME}`}
    // CCR3-5293 Spoofing ALIS- Unsecured & Secured Calls
    if (data?.row?.simulationType == Constants.SIMULATION_TYPES.SECURED_WEBMESSAGE) {
      this.webMessageSimulation(data,pptAttributes);
    }
    else {
      this.simulatingALISAttributesCalls(data,pptAttributes);
    }
  }

  /**
   * Simulates ALIS attribute calls by preparing and injecting scenario-specific attributes
   * into a simulation payload based on the provided simulation type and customer data.
   *
   * This method modifies the simulation payload according to the simulation type, sets
   * various participant attributes (such as security level, customer ID, queue name, etc.),
   * and triggers the identification of the call as inbound or outbound via the Genesys service.
   *
   * @param data - The input data object containing customer and simulation details. Expected to have a `row` property
   *               with fields such as `customerType`, `platformExternalId`, `clientId`, `SIM_clientName`, `simulationType`,
   *               `udpId`, `firstName`, and `lastName`.
   *
   * @remarks
   * - Updates `rootService.isLiveCall` and, for callback calls, `rootService.isCallBackCall`.
   * - Encrypts and encodes the customer ID if necessary.
   * - Sets up participant attributes including security level, ANI, direction, and other metadata.
   * - Assigns the prepared attributes to `customerService.liveCall` and client ID to `customerService.liveClient`.
   * - Generates a unique conversation ID and notifies the Genesys service.
   * - Alerts the user if an unsupported simulation type is provided.
   * A cryptographic or "random" UUID (Universally Unique Identifier) is a Version 4 UUID. It has a total length of 36 characters and follows a specific 8-4-4-4-12 pattern, with 32 hexadecimal digits and four hyphens
   */
  simulatingALISAttributesCalls(data: any,pptAttributes?:any) {

    const customerType = data?.row?.customerType || 'ACH';
    const simulationType = data?.row?.simulationType;
    const Config_DesktopIndicator = this.rootService.LOB_methods?.find((item: any) => item.authModels === data.row.authModels)?.lobTypes?.[0] || 'N';

    this.rootService.isLiveCall = true


      let simulationPayload;
      let attributes;

      // Unified switch-case for selecting JSON and injecting scenario-specific attributes 

      switch (simulationType) {      

        case Constants.SIMULATION_TYPES.SECURED_INBOUND_CALL:

          simulationPayload = ALIS_SECURED_INBOUND_CONVERSATION;
          attributes = simulationPayload.participants[0].attributes;
          attributes.PINV = 'S';
          attributes.CS_SECURITY_LEVEL = '4';
          if (data.row.authModels === Constants.AuthMethods['IND-DATA']) {
            attributes["CP_INDICATIVE_DATA_SECURED"] = "true";
            attributes["CS_AUTH_CD"] = Constants.AuthCode['IND-DATA'];
          }
          else {
            attributes[Constants.ALIS_ATTRIBUTE_NAMES.CIM_DATA] = JSON.stringify({ identifiers: ["TXPY-ID", "BRTH-DT", "ZIP-CD"], authMethods: ["PIN"] });
          }
          attributes.Participant_ANI = Constants.SIM_PARTICIPANT_ANI;
          simulationPayload.participants[0].address= Constants.SIM_PARTICIPANT_ANI;
          simulationPayload.participants[0].direction = Constants.SIM_INBOUND_DIRECTION;
          break;

        case Constants.SIMULATION_TYPES.UNSECURED_INBOUND_CALL:

          simulationPayload = ALIS_UNSECURED_INBOUND_CONVERSATION;
          attributes = simulationPayload.participants[0].attributes;
          attributes.PINV = 'U';
          attributes.CS_SECURITY_LEVEL = '2';
          attributes.Participant_ANI = Constants.SIM_PARTICIPANT_ANI;
          simulationPayload.participants[0].address= Constants.SIM_PARTICIPANT_ANI;
          simulationPayload.participants[0].direction = Constants.SIM_INBOUND_DIRECTION;
          break;

        case Constants.SIMULATION_TYPES.UNSECURED_CALLBACK_CALL:

          this.rootService.isCallBackCall=true;
          simulationPayload = ALIS_UNSECURE_CALLBACK_CONVERSATION;
          attributes = simulationPayload.participants[0].attributes;
          attributes.PINV = 'U';
          attributes.CS_SECURITY_LEVEL = '2';
          simulationPayload.participants[0].address= Constants.SIM_PARTICIPANT_ANI;
          simulationPayload.participants[0].direction = Constants.SIM_OUTBOUND_DIRECTION;
          break;

        case Constants.SIMULATION_TYPES.UNKNOWN_OUTBOUND_CALL:
        
          simulationPayload = ALIS_UNKNOWN_OUTBOUND_CONVERSATION;
          attributes = simulationPayload.participants[0].attributes;
          attributes.PINV = '';
          attributes.CS_SECURITY_LEVEL = '0';
          simulationPayload.participants[0].address= Constants.SIM_PARTICIPANT_ANI;
          simulationPayload.participants[0].direction = Constants.SIM_OUTBOUND_DIRECTION;
          break;

        case Constants.SIMULATION_TYPES.UNKNOWN_INBOUND_CALL:
        
          simulationPayload = ALIS_UNKNOWN_INBOUND_CONVERSATION;
          attributes = simulationPayload.participants[0].attributes;
          attributes.PINV = '';
          attributes.CS_SECURITY_LEVEL = '0';
          simulationPayload.participants[0].address= Constants.SIM_PARTICIPANT_ANI;
          simulationPayload.participants[0].direction = Constants.SIM_INBOUND_DIRECTION;
          break;

        case Constants.SIMULATION_TYPES.UNKNOWN_CALLBACK_CALL:
          
          this.rootService.isCallBackCall=true;
          simulationPayload = ALIS_UNKNOWN_INBOUND_CONVERSATION;
          attributes = simulationPayload.participants[0].attributes;
          attributes.PINV = '';
          attributes.CS_SECURITY_LEVEL = '0';
          simulationPayload.participants[0].address= Constants.SIM_PARTICIPANT_ANI;
          simulationPayload.participants[0].direction = Constants.SIM_OUTBOUND_DIRECTION;
          break;

        default:
          alert('Unsupported simulation type');
          return;

      }

    if (simulationType === Constants.SIMULATION_TYPES.UNKNOWN_OUTBOUND_CALL) {
      attributes.ENC_CS_CUSTOMER_ID = '';
      attributes.UDP_GBL_ID = '';
    } else {
      attributes.ENC_CS_CUSTOMER_ID = this.encryptDecryptService.encodeAndEncrypt(
        pptAttributes?.platformExternalId?.length > 12 ? this.encryptDecryptService.decrypt(pptAttributes?.platformExternalId) : pptAttributes?.platformExternalId
      );
      attributes.UDP_GBL_ID = data?.row?.udpId;
    }

     
      attributes.IS_CS_CUSTOMER_ID_ENCRYPTED = "true";
    
      attributes.QUEUE_NAME = pptAttributes?.queueName;
      attributes.CS_CLIENT_ID = pptAttributes?.clientId;
      attributes['PPT-NAME'] = `${data?.row?.firstName}-${data?.row?.lastName}`;
      attributes.CS_XFER_USER_ID = this.rootService.lanId;
      attributes.CS_INIT_REP_ID = this.rootService.lanId;
      attributes.CUSTOMER_TYPE=customerType;
      attributes.CS_CALLER_TYPE=customerType;
      attributes.Config_DesktopIndicator=Config_DesktopIndicator;
      simulationPayload.participants[0].connectedTime = new Date().toISOString();
      this.customerService.liveCall = simulationPayload.participants[0].attributes;
      this.customerService.liveClient = data?.row?.clientId;
      const uuid = crypto.randomUUID();
      simulationPayload.id = uuid.replace(/-/g, '');
      const startTime = new Date();
      this.rootService.simulationRecords?.push({ fullName: (data?.row?.lastName) ? (`${data?.row?.lastName}, ${data?.row?.firstName}`) : (data?.row?.simulationType), channel: Constants.conversion.call, conversationAttributes: simulationPayload.participants[0].attributes, conversationId: simulationPayload.id, startTime: startTime });
      let response = { conversationId: simulationPayload.id, clientValue: simulationPayload.participants[0], liveUser: simulationPayload.participants[0] };
      this.rootService.genesysCallObj = { eventBody: simulationPayload };
      this.genesysService.identifyInboundOrOutBoundCall(response.liveUser, response.clientValue, response.conversationId);


  }

  // CCR3-5082 Spoofing ALIS- Web Message

  /**
 * Simulates a web-message (chat) conversation by preparing and publishing live chat details.
 *
 * This method performs the following actions:
 * - Marks the application state as a live chat (rootService.isLiveChat = true).
 * - Loads the ALIS web-message conversation template (ALIS_WEB_MESSAGE_CONVERSATION) and
 *   updates the first participant's attributes with provided pptAttributes and data.
 * - Generates a unique conversation ID (using crypto.randomUUID) and assigns it to
 *   rootService.conversationId.
 * - Calls genesysService.navigateToCustTab(...) to navigate to the customer tab with the
 *   prepared participant payload.
 * - Sets the participant connectedTime to the current time.
 * - Publishes the prepared live chat payload via rootService.liveChatDetails.next(...) so
 *   other parts of the application can react to the new live chat.
 *
 * @param data - Input data object expected to contain row (e.g. data.row.udpId).
 * @param pptAttributes - pptattributes object (optional). Expected fields:
 *                        { platformExternalId?: string, clientId?: string, queueName?: string }
 *
 * @remarks
 * - This method mutates the ALIS_WEB_MESSAGE_CONVERSATION template (its participant attributes
 *   and connectedTime).
 * - Ensure pptAttributes and data are validated before calling (no null/undefined assumptions).
 * - Be mindful not to log or expose sensitive identifiers (udpId, platformExternalId, etc.).
 */
  webMessageSimulation(data: any, pptAttributes?: any) {


    this.rootService.isLiveChat = true;
    let chatObj = ALIS_WEB_MESSAGE_CONVERSATION;
    let attributes = chatObj.participants[0].attributes;
    const uuid = crypto.randomUUID();
    const id = uuid.replace(/-/g, '');
   

    this.genesysService.navigateToCustTab(chatObj.participants[0]);
    this.rootService.conversationId = id;
    attributes['QUEUE_NAME'] = pptAttributes?.queueName;
    attributes['context.ClientID'] = pptAttributes?.clientId;
    attributes['context.glblPrsnId'] = data?.row?.udpId;
    chatObj.participants[0].connectedTime = new Date().toISOString();
    const startTime = new Date();
    this.rootService.simulationRecords.push({ fullName: `${data?.row?.lastName}, ${data?.row?.firstName}`, channel: Constants.conversion.chat, conversationAttributes: JSON.parse(JSON.stringify(chatObj.participants[0].attributes)), conversationId: id, startTime: startTime });

    // Add chat simulation to chatConversations Map for proper multiple chat handling
    this.rootService.chatConversations.set(id, {
      conversationId: id,
      participant: chatObj.participants[0],
      isSimulation: true
    });

    this.rootService.liveChatDetails.next({
      clientValue: chatObj.participants[0],
      conversationId: id,
      liveUser: chatObj.participants[0]
    });

  }

						/**
   * Ends an active simulation by unique conversationId to prevent race conditions.
   * 
   * This method safely terminates simulations in scenarios where multiple simulations
   * might be ended rapidly in succession (e.g., via UI interactions). Using conversationId
   * instead of array index prevents misaligned indices when splice() operations occur.
   * 
   * @param conversationId - Unique identifier for the simulation conversation
   * 
   * **Process Flow:**
   * 1. **Safe Record Lookup**: Uses `findIndex()` with conversationId to locate simulation record
   * 2. **Validation**: Returns early if simulation record not found (defensive programming)
   * 3. **Duration Calculation**: Computes simulation duration from stored startTime to current endTime
   * 4. **Genesys Payload Construction**: Creates standardized payload with disconnected state
   * 5. **Channel-Specific Termination**: Routes to appropriate GenesysCloud service method
   * 6. **Safe Cleanup**: Removes record using the safely-located index
   * 
   * **Channel Handling:**
   * - For CALL simulations: Uses `genesysService.genesysCall()` to trigger proper call termination flow
   * - For CHAT simulations: Uses `genesysService.genesysChat()` to trigger proper chat wrap-up flow
   * - For unknown channels: Logs warning but still performs cleanup
   * 
   * **Thread Safety:**
   * - Eliminates race condition where rapid splice() operations could cause index misalignment
   * - Each conversationId is unique, ensuring the correct simulation is always terminated
   * - Graceful handling when simulation record is not found (already removed or invalid ID)
   * 
   * @example
   * ```typescript
   * // Safe: Uses unique identifier instead of potentially stale index
   * simulationService.endSimulation(simRecord.conversationId);
   * ```
   * 
   * @see {@link GenesysCloudService.genesysCall} - For call termination handling
   * @see {@link GenesysCloudService.genesysChat} - For chat termination and wrap-up
   * 
   * @since CCR3-1950 - Enhanced with conversationId-based lookup for thread safety
   */
  endSimulation(conversationId: string) {
    const index = this.rootService.simulationRecords.findIndex(r => r.conversationId === conversationId);
    if (index === -1) {
      console.warn(`Simulation with conversationId ${conversationId} not found`);
      return;
    }
    
    const record = this.rootService.simulationRecords[index];
    const endTime = new Date();
    const startTime = record.startTime;
    
    // Construct common objects for both call and chat termination
    const liveUser = { 
      endTime: endTime.toISOString(), 
      connectedTime: startTime.toISOString(), 
      state: Constants.ALIS_Live_Status.disconnected 
    };
    
    const entireGenesysObj = { 
      'eventBody': { 
        'id': conversationId, 
        'participants': [{ 
          purpose: 'customer', 
          state: Constants.ALIS_Live_Status.disconnected, 
          attributes: record.conversationAttributes 
        }] 
      } 
    };
    
    // Handle call simulation end
    if (record.channel === Constants.conversion.call) {
      this.genesysService.genesysCall(liveUser, entireGenesysObj);
      
    }
    else if (record.channel === Constants.conversion.chat) {
      // Handle chat simulation end
      this.genesysService.genesysChat(liveUser, entireGenesysObj);
    }
    else {
      console.warn(`Unknown simulation channel type: ${record.channel}`);
    }
     if(this.simulationComponentRef && (record.channel === Constants.conversion.call || this.rootService.simulationRecords.length==this.rootService.CCTConfigs.MAXMIUM_ACCOUNTS)){
        this.disableSimulationForm();
      }
    this.rootService.simulationRecords.splice(index, 1);
  }
disableSimulationForm(self?) {
  if(self)
    this.simulationComponentRef=self;
  let component=this.simulationComponentRef;
    Object.keys(component.customerSearchForm.controls).forEach(Key => {
      if (Key){
       if(self){
          self.customerSearchForm.get(Key)?.setValue('');
          self.customerSearchForm.get(Key)?.disable();
          }
          else{
            component.customerSearchForm.get(Key)?.enable();
          }
        }
    
        component.customerSearchForm.get(Key)?.updateValueAndValidity();
      
    });
  
}

}